package co.maytes.standupbot.dashboard.features.releases

sealed interface MdInline {
    data class Text(val value: String) : MdInline

    data class Code(val value: String) : MdInline

    data class Bold(val children: List<MdInline>) : MdInline

    data class Italic(val children: List<MdInline>) : MdInline

    data class Link(val label: List<MdInline>, val href: String) : MdInline
}

sealed interface MdBlock {
    data class Heading(val level: Int, val content: List<MdInline>) : MdBlock

    data class Paragraph(val content: List<MdInline>) : MdBlock

    data class Quote(val content: List<MdInline>) : MdBlock

    data class CodeBlock(val code: String) : MdBlock

    data class ListBlock(val ordered: Boolean, val items: List<MdListItem>) : MdBlock

    data object Rule : MdBlock
}

data class MdListItem(val content: List<MdInline>, val children: List<MdBlock.ListBlock> = emptyList())

object ReleaseMarkdown {
    private val headingLine = Regex("^(#{1,6})\\s+(.*?)\\s*#*\\s*$")
    private val bulletLine = Regex("^(\\s*)([-*+])\\s+(.*)$")
    private val orderedLine = Regex("^(\\s*)(\\d+)[.)]\\s+(.*)$")
    private val ruleLine = Regex("^\\s{0,3}([-*_])(\\s*\\1){2,}\\s*$")
    private val fenceLine = Regex("^\\s*(```|~~~)")
    private val htmlComment = Regex("<!--[\\s\\S]*?-->")
    private val htmlTag = Regex("</?(details|summary|p|br|div|span|img|picture|source|sup|sub|kbd|center|h[1-6])\\b[^>]*>", RegexOption.IGNORE_CASE)
    private val image = Regex("!\\[([^\\]]*)\\]\\(([^)\\s]+)[^)]*\\)")
    private val linkPattern = Regex("^\\[([^\\]]+)\\]\\(([^)\\s]+)(?:\\s+\"[^\"]*\")?\\)")
    private val bareUrl = Regex("^https?://[^\\s<>()\\[\\]]+")
    private val mention = Regex("^@([A-Za-z0-9](?:[A-Za-z0-9-]{0,38}))")
    private val trailingPunctuation = charArrayOf('.', ',', ';', ':', '!', '?')
    private const val INDENT_STEP = 2

    fun parse(source: String): List<MdBlock> {
        val lines =
            source
                .replace("\r\n", "\n")
                .replace(htmlComment, "")
                .replace(image) { "[${it.groupValues[1].ifBlank { "image" }}](${it.groupValues[2]})" }
                .replace(htmlTag, "")
                .split('\n')
        val blocks = mutableListOf<MdBlock>()
        var index = 0
        while (index < lines.size) {
            val line = lines[index]
            when {
                line.isBlank() -> index++
                fenceLine.containsMatchIn(line) -> index = readFence(lines, index, blocks)
                ruleLine.matches(line) -> {
                    blocks += MdBlock.Rule
                    index++
                }
                headingLine.matches(line) -> {
                    val match = headingLine.matchEntire(line)!!
                    blocks += MdBlock.Heading(match.groupValues[1].length, inlines(match.groupValues[2]))
                    index++
                }
                line.trimStart().startsWith(">") -> index = readQuote(lines, index, blocks)
                isListLine(line) -> index = readList(lines, index, blocks)
                else -> index = readParagraph(lines, index, blocks)
            }
        }
        return blocks
    }

    fun plainText(blocks: List<MdBlock>): String =
        blocks.joinToString(" ") { block ->
            when (block) {
                is MdBlock.Heading -> flatten(block.content)
                is MdBlock.Paragraph -> flatten(block.content)
                is MdBlock.Quote -> flatten(block.content)
                is MdBlock.CodeBlock -> block.code
                is MdBlock.ListBlock -> block.items.joinToString(" ") { flatten(it.content) }
                MdBlock.Rule -> ""
            }
        }

    fun safeHref(href: String): String? = href.takeIf { it.startsWith("https://") || it.startsWith("http://") }

    private fun flatten(content: List<MdInline>): String =
        content.joinToString("") { inline ->
            when (inline) {
                is MdInline.Text -> inline.value
                is MdInline.Code -> inline.value
                is MdInline.Bold -> flatten(inline.children)
                is MdInline.Italic -> flatten(inline.children)
                is MdInline.Link -> flatten(inline.label)
            }
        }

    private fun isListLine(line: String): Boolean = bulletLine.matches(line) || orderedLine.matches(line)

    private fun readFence(
        lines: List<String>,
        start: Int,
        out: MutableList<MdBlock>,
    ): Int {
        val body = mutableListOf<String>()
        var index = start + 1
        while (index < lines.size && !fenceLine.containsMatchIn(lines[index])) {
            body += lines[index]
            index++
        }
        out += MdBlock.CodeBlock(body.joinToString("\n"))
        return minOf(index + 1, lines.size)
    }

    private fun readQuote(
        lines: List<String>,
        start: Int,
        out: MutableList<MdBlock>,
    ): Int {
        val body = mutableListOf<String>()
        var index = start
        while (index < lines.size && lines[index].trimStart().startsWith(">")) {
            body += lines[index].trimStart().removePrefix(">").trim()
            index++
        }
        out += MdBlock.Quote(inlines(body.joinToString(" ").trim()))
        return index
    }

    private fun readParagraph(
        lines: List<String>,
        start: Int,
        out: MutableList<MdBlock>,
    ): Int {
        val body = mutableListOf<String>()
        var index = start
        while (index < lines.size && lines[index].isNotBlank() && !startsNewBlock(lines[index])) {
            body += lines[index].trim()
            index++
        }
        out += MdBlock.Paragraph(inlines(body.joinToString(" ")))
        return index
    }

    private fun startsNewBlock(line: String): Boolean =
        headingLine.matches(line) ||
            fenceLine.containsMatchIn(line) ||
            ruleLine.matches(line) ||
            line.trimStart().startsWith(">") ||
            isListLine(line)

    private fun readList(
        lines: List<String>,
        start: Int,
        out: MutableList<MdBlock>,
    ): Int {
        val entries = mutableListOf<ListEntry>()
        var index = start
        while (index < lines.size) {
            val line = lines[index]
            if (line.isBlank()) {
                val next = lines.getOrNull(index + 1)
                if (next != null && isListLine(next) && indentOf(next) > 0) {
                    index++
                    continue
                }
                break
            }
            val entry = listEntry(line)
            if (entry == null) {
                if (entries.isEmpty() || startsNewBlock(line) || !line.first().isWhitespace()) break
                entries[entries.lastIndex] = entries.last().copy(text = entries.last().text + " " + line.trim())
            } else {
                entries += entry
            }
            index++
        }
        if (entries.isNotEmpty()) out += buildListBlock(entries, entries.first().indent)
        return index
    }

    private data class ListEntry(val indent: Int, val ordered: Boolean, val text: String)

    private fun indentOf(line: String): Int = line.length - line.trimStart().length

    private fun listEntry(line: String): ListEntry? {
        bulletLine.matchEntire(line)?.let { return ListEntry(it.groupValues[1].length, false, it.groupValues[3]) }
        orderedLine.matchEntire(line)?.let { return ListEntry(it.groupValues[1].length, true, it.groupValues[3]) }
        return null
    }

    private fun buildListBlock(
        entries: List<ListEntry>,
        baseIndent: Int,
    ): MdBlock.ListBlock {
        val items = mutableListOf<MdListItem>()
        var index = 0
        while (index < entries.size) {
            val entry = entries[index]
            val childStart = index + 1
            var childEnd = childStart
            while (childEnd < entries.size && entries[childEnd].indent >= entry.indent + INDENT_STEP) childEnd++
            val children =
                if (childEnd > childStart) listOf(buildListBlock(entries.subList(childStart, childEnd), entries[childStart].indent)) else emptyList()
            items += MdListItem(inlines(entry.text), children)
            index = childEnd
        }
        return MdBlock.ListBlock(ordered = entries.first { it.indent <= baseIndent }.ordered, items = items)
    }

    fun inlines(text: String): List<MdInline> {
        val out = mutableListOf<MdInline>()
        val plain = StringBuilder()

        fun flush() {
            if (plain.isNotEmpty()) {
                out += MdInline.Text(plain.toString())
                plain.clear()
            }
        }
        var index = 0
        while (index < text.length) {
            val rest = text.substring(index)
            val c = text[index]
            when {
                c == '\\' && index + 1 < text.length && !text[index + 1].isLetterOrDigit() -> {
                    plain.append(text[index + 1])
                    index += 2
                }
                c == '`' -> {
                    val end = text.indexOf('`', index + 1)
                    if (end > index) {
                        flush()
                        out += MdInline.Code(text.substring(index + 1, end))
                        index = end + 1
                    } else {
                        plain.append(c)
                        index++
                    }
                }
                rest.startsWith("**") || rest.startsWith("__") -> {
                    val marker = rest.substring(0, 2)
                    val end = text.indexOf(marker, index + 2)
                    if (end > index + 2) {
                        flush()
                        out += MdInline.Bold(inlines(text.substring(index + 2, end)))
                        index = end + 2
                    } else {
                        plain.append(marker)
                        index += 2
                    }
                }
                (c == '*' || c == '_') && isEmphasisStart(text, index) -> {
                    val end = text.indexOf(c, index + 1)
                    if (end > index + 1) {
                        flush()
                        out += MdInline.Italic(inlines(text.substring(index + 1, end)))
                        index = end + 1
                    } else {
                        plain.append(c)
                        index++
                    }
                }
                c == '[' -> {
                    val match = linkPattern.find(rest)
                    if (match != null) {
                        flush()
                        out += MdInline.Link(inlines(match.groupValues[1]), match.groupValues[2])
                        index += match.value.length
                    } else {
                        plain.append(c)
                        index++
                    }
                }
                c == 'h' && bareUrl.containsMatchIn(rest) && (index == 0 || !text[index - 1].isLetterOrDigit()) -> {
                    val raw = bareUrl.find(rest)!!.value
                    val url = raw.trimEnd(*trailingPunctuation)
                    flush()
                    out += MdInline.Link(listOf(MdInline.Text(shortUrl(url))), url)
                    index += url.length
                }
                c == '@' && (index == 0 || !text[index - 1].isLetterOrDigit()) && mention.containsMatchIn(rest) -> {
                    val name = mention.find(rest)!!.value
                    flush()
                    out += MdInline.Link(listOf(MdInline.Text(name)), "https://github.com/${name.removePrefix("@")}")
                    index += name.length
                }
                else -> {
                    plain.append(c)
                    index++
                }
            }
        }
        flush()
        return out
    }

    private fun isEmphasisStart(
        text: String,
        index: Int,
    ): Boolean {
        val next = text.getOrNull(index + 1) ?: return false
        val before = text.getOrNull(index - 1)
        return !next.isWhitespace() && (before == null || !before.isLetterOrDigit())
    }

    private val pullPath = Regex("^https://github\\.com/([^/]+/[^/]+)/(pull|issues)/(\\d+)$")
    private val comparePath = Regex("^https://github\\.com/([^/]+/[^/]+)/compare/(.+)$")

    fun shortUrl(url: String): String {
        pullPath.matchEntire(url)?.let { return "#${it.groupValues[3]}" }
        comparePath.matchEntire(url)?.let { return it.groupValues[2] }
        return url.removePrefix("https://").removePrefix("http://").removePrefix("www.")
    }
}
